import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import * as store from "../state/store.js";
import { SignalWatcher } from "../state/signal-watcher.js";
import {
  deleteSelectedLayers,
  mergeSelectedLayers,
} from "../ops/layers.js";
import { confirmDialog, openModal } from "../ops/ui.js";
import { moveSelectedToLayer } from "../ops/entities.js";
import { tooltip } from "../utils/tooltip.js";

@customElement("layer-toolbar")
export class LayerToolbar extends SignalWatcher(LitElement) {
  static override styles = css`
    :host {
      display: flex;
      gap: 4px;
      padding: 6px 12px;
      background: var(--bg-tertiary);
      border-bottom: 1px solid var(--border-color);
      align-items: center;
      flex-wrap: wrap;
      min-height: 40px;
    }

    button {
      background: transparent;
      border: none;
      color: var(--text-secondary);
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 14px;
      transition: background 0.15s, color 0.15s;
    }

    button:hover:not(:disabled) {
      background: var(--bg-hover);
      color: var(--text-primary);
    }

    button.danger:hover:not(:disabled) {
      background: var(--danger);
      color: #fff;
    }

    button:disabled { opacity: 0.3; cursor: not-allowed; }

    .separator {
      width: 1px;
      height: 20px;
      background: var(--border-color);
      margin: 0 2px;
    }

    input.search {
      flex: 1;
      min-width: 60px;
      padding: 4px 8px;
      background: var(--bg-secondary);
      border: 1px solid var(--border-color);
      border-radius: 4px;
      color: var(--text-primary);
      font-size: 11px;
      outline: none;
    }

    input.search:focus { border-color: var(--accent); }
    input.search::placeholder { color: var(--text-secondary); }
  `;

  override render() {
    const hasSelection = store.selectedEntityIds().size > 0;
    const selectedLayers = store.selectedLayerNames();
    const layers = store.layers();
    // A layer is only deletable when it isn't the default AND isn't locked.
    const deletable = Array.from(selectedLayers).filter(
      (n) => n !== "0" && !layers[n]?.locked
    );
    const hasDeletable = deletable.length > 0;
    const hasNonDefault = Array.from(selectedLayers).some((n) => n !== "0");

    return html`
      <button
        ${tooltip("Add Layer")}
        aria-label="Add Layer"
        @click=${this.#onAdd}
      >+</button>
      <button
        class="danger"
        ${tooltip(hasDeletable
          ? "Delete Selected Layers"
          : "No deletable layers selected (locked or default)")}
        aria-label="Delete Selected Layers"
        ?disabled=${!hasDeletable}
        @click=${this.#onDelete}
      >
        🗑
      </button>
      <div class="separator"></div>
      <button
        ${tooltip("Move Selected Entities to Layer")}
        aria-label="Move Selected Entities to Layer"
        ?disabled=${!hasSelection}
        @click=${this.#onMove}
      >
        📎
      </button>
      <button
        ${tooltip("Merge Selected Layers into 0")}
        aria-label="Merge Selected Layers into 0"
        ?disabled=${!hasNonDefault}
        @click=${mergeSelectedLayers}
      >
        🔀
      </button>
      <div class="separator"></div>
      <input
        class="search"
        type="text"
        placeholder="Filter..."
        .value=${store.filterText()}
        @input=${(e: Event) => store.write.filterText((e.target as HTMLInputElement).value)}
      />
    `;
  }

  #onAdd = () => openModal("add-layer", {});

  #onMove = () => {
    if (store.layerOrder().length === 0) return;
    openModal("move-to-layer", { onConfirm: moveSelectedToLayer });
  };

  #onDelete = () => {
    const layers = store.layers();
    const names = Array.from(store.selectedLayerNames()).filter(
      (n) => n !== "0" && !layers[n]?.locked
    );
    if (names.length === 0) return;
    const count = names.length;
    const title = count === 1
      ? `Delete layer "${names[0]}"?`
      : `Delete ${count} layers?`;
    const message = count === 1
      ? `Its entities will be moved to layer "0". This can be undone with Ctrl+Z.`
      : `All entities on the selected layers will be moved to layer "0". This can be undone with Ctrl+Z.`;
    confirmDialog({
      title,
      message,
      confirmLabel: "Delete",
      danger: true,
      onConfirm: deleteSelectedLayers,
    });
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "layer-toolbar": LayerToolbar;
  }
}
